namespace $ {

	const repair_pending = new Map< string, Promise< void > >

	export function $shm_hitalama_baza_diff_cache_unsealed(
		units: readonly $giper_baza_unit[],
	) {
		const covered = new Map< string, Set< string > >
		for( const unit of units ) {
			if( !( unit instanceof $giper_baza_unit_seal ) ) continue
			let hashes = covered.get( unit.lord().str )
			if( !hashes ) covered.set( unit.lord().str, hashes = new Set )
			for( const hash of unit.hash_list() ) hashes.add( hash.str )
		}

		return units.filter( unit => {
			if( !( unit instanceof $giper_baza_unit_sand ) ) return false
			if( !unit.encoded() ) return false
			return !( covered.get( unit.lord().str )?.has( unit.hash().str ) ?? false )
		} )
	}

	export function $shm_hitalama_baza_diff_cache_recover(
		host: $giper_baza_land,
		units: readonly $giper_baza_unit[],
	): Promise< void > {
		const key = `${ host.link().str }:${ units.map( unit => unit.path() ).join( '|' ) }`
		const pending = repair_pending.get( key )
		if( pending ) return pending

		const mine = host.mine()
		const repair = $mol_wire_async( mine ).units_save({ ins: [], del: units }).then( ()=> {
			for( const unit of units ) mine.units_persisted.delete( unit )
			$shm_hitalama_resilience_report( 'local_cache', new Error( 'Invalid cached units removed' ), host.link().str )
		} ).catch( error => {
			$shm_hitalama_resilience_report( 'application', error, `local cache ${ host.link().str }` )
			throw error
		} ).finally( ()=> repair_pending.delete( key ) )
		repair_pending.set( key, repair )
		return repair
	}

	/** Quarantines corrupt cached units. The normal local-first sync obtains valid data later. */
	export function $shm_hitalama_baza_diff_cache_repair(
		native: $giper_baza_land['diff_apply'],
		host: $giper_baza_land,
		units: readonly $giper_baza_unit[],
		skip_load?: 'skip_load',
		recover = $shm_hitalama_baza_diff_cache_recover,
	): ReturnType< $giper_baza_land['diff_apply'] > {
		let failure: Error | null = null
		try {
			return $shm_hitalama_baza_diff_rank_retry< ReturnType< $giper_baza_land['diff_apply'] > >( native, host, units, skip_load )
		} catch( error ) {
			if( error instanceof Promise ) throw error
			if( !( error instanceof Error ) ) throw error
			if( error.message !== 'No Seal for Sand' && error.message !== 'No Seal for Gift' ) throw error
			if( skip_load !== 'skip_load' ) throw error
			failure = error
			if( error.message === 'No Seal for Gift' ) {
				if( !( error.cause instanceof $giper_baza_unit_gift ) ) throw error
				// Quarantine only the invalid unit. Valid and possibly not-yet-synced
				// local changes in the same land must never be discarded with it.
				throw recover( host, [ error.cause ] )
			}
		}

		const broken = $shm_hitalama_baza_diff_cache_unsealed( units )
		if( !broken.length ) throw failure!
		throw recover( host, broken )
	}

	/** Keep the original network/cache distinction and all native rights audits. */
	export function $shm_hitalama_baza_diff_cache_verified(
		native: $giper_baza_land['diff_apply'],
		host: $giper_baza_land,
		units: readonly $giper_baza_unit[],
		skip_load?: 'skip_load',
	) {
		$mol_wire_sync( $shm_hitalama_baza_replay ).prepare( host, units, skip_load )
		// Do not substitute skip_load after preparation: doing so would misclassify
		// an incomplete network delta as corrupt local cache eligible for deletion.
		return $shm_hitalama_baza_diff_cache_repair( native, host, units, skip_load )
	}

	export function $shm_hitalama_baza_diff_cache_repair_install() {
		const proto = $giper_baza_land.prototype as $giper_baza_land & {
			$shm_hitalama_diff_apply_native?: $giper_baza_land['diff_apply']
		}
		if( proto.$shm_hitalama_diff_apply_native ) return

		const native = proto.diff_apply
		proto.$shm_hitalama_diff_apply_native = native
		proto.diff_apply = function( units, skip_load ) {
			return $shm_hitalama_baza_diff_cache_verified( native, this, units, skip_load )
		}
	}

}
